//? Importing necessary modules
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  FormEvent,
} from "react";

//? Importing necessary modules from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

// * Importing necessary modules from react-select
import AsyncSelect from "react-select/async";

//? Importing necessary modules from react-select
import { MultiValue } from "react-select";

//? Importing necessary modules from react-bootstrap
import { Form, Button, Modal, Row, Col } from "react-bootstrap";

//* Importing necessary modules from react-transition-group
import { CSSTransition, TransitionGroup } from "react-transition-group";

// * Importing necessary modules from react-icons
import { RiArrowGoBackLine } from "react-icons/ri";
import { FaUsers } from "react-icons/fa";

//* Importing necessary components
import DefaultButton from "../../../../component/base/DefaultButton";
import { Attention } from "../../../../component/base/Attention";
import { Searchbar } from "../../../../component/list/searchbar";
import { Actions } from "../../../../component/list/actions";

//? Importing necessary hooks
import { useTeams } from "../../../../hooks/teams/useTeams";
import { useCourses } from "../../../../hooks/courses/useCourses";
import { useUnits } from "../../../../hooks/units/useUnits";
import { useRegistrations } from "../../../../hooks/registration/useRegistration";
import { useApplicationStudents } from "../../../../hooks/applications/applicationsStudents/useApplicationStudent";

//? Importing necessary types
import {
  type SelectedStudentData,
  type Registration,
} from "../students/types/types";
import { type Team } from "../../../../hooks/teams/types/types";

//* Importing necessary types
type StudentSimple = {
  id: string;
  name: string;
  email: string;
};

// Unifica dados do Team (Turma), garantindo um padrão (course, unit, etc).
function unifyTeamData(rawTeam: any) {
  const courseObj =
    typeof rawTeam.course === "string"
      ? { id: rawTeam.course, name: rawTeam.course }
      : { id: rawTeam.course?.id, name: rawTeam.course?.name };

  const unitObj =
    typeof rawTeam.unit === "string"
      ? { id: rawTeam.unit, name: rawTeam.unit }
      : { id: rawTeam.unit?.id, name: rawTeam.unit?.name };

  const isFirstSemester =
    rawTeam.is_first_semester ?? rawTeam.isFirstSemester ?? false;

  return {
    ...rawTeam,
    course: courseObj,
    unit: unitObj,
    isFirstSemester,
  };
}

// Retorna lista única de estudantes a partir das registrations.
function getUniqueStudentsWithTeam(
  registrations: Registration[]
): SelectedStudentData[] {
  const seenIds = new Set<string>();
  const result: SelectedStudentData[] = [];

  for (const reg of registrations) {
    const uniqueKey = reg.studentId;
    if (!seenIds.has(uniqueKey)) {
      seenIds.add(uniqueKey);
      result.push({
        ...reg.student,
        id: uniqueKey,
        team: reg.team ? unifyTeamData(reg.team) : undefined,
      });
    }
  }
  return result;
}

// Função de debounce simples.
function debounce<Func extends (...args: any[]) => any>(
  func: Func,
  wait: number
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<Func>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => func(...args), wait);
  };
}

// Função para checar duplicados.
function checkForDuplicatesAndFilter(
  candidates: SelectedStudentData[],
  alreadyInAppSet: Set<string>,
  selectedList: SelectedStudentData[]
): { uniqueNew: SelectedStudentData[]; duplicates: SelectedStudentData[] } {
  const duplicates = candidates.filter(
    (cand) =>
      alreadyInAppSet.has(cand.id) ||
      selectedList.some(
        (selStu) => selStu.id === cand.id || selStu.email === cand.email
      )
  );

  const uniqueNew = candidates.filter(
    (cand) =>
      !duplicates.some((dup) => dup.id === cand.id || dup.email === cand.email)
  );

  return { uniqueNew, duplicates };
}

export function ApplicationManagerFormStudent() {
  const navigate = useNavigate();
  const params = useParams();

  // Identificador da aplicação
  const examId = params.id || "";
  const token = localStorage.getItem("token") || "";

  // Filtros
  const [filterCourseId, setFilterCourseId] = useState<string | undefined>();
  const [filterUnitId, setFilterUnitId] = useState<string | undefined>();

  // Turmas selecionadas via AsyncSelect (Turmas)
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);

  // Texto de busca para filtrar na tabela final
  const [searchText, setSearchText] = useState("");

  // Lista local de alunos a serem adicionados
  const [selectedStudents, setSelectedStudents] = useState<
    SelectedStudentData[]
  >([]);

  // Opções do AsyncSelect (alunos individuais) atualmente selecionadas
  const [selectedStudentOptions, setSelectedStudentOptions] = useState<
    SelectedStudentData[]
  >([]);

  // Alerta de duplicado (modal)
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);

  // Hooks
  const { courses } = useCourses(token);
  const { units } = useUnits(token);
  const { teams, setSearch: setTeamsHookSearch, getTeamById } = useTeams(token);
  const { registrations, setFilters: setRegistrationsFilters } =
    useRegistrations(token);
  const {
    applicationStudents,
    loading: loadingAppStudents,
    createApplicationStudent,
    deleteApplicationStudent,
  } = useApplicationStudents(examId, token);

  // Lista única de estudantes, memoizada para não recalcular toda hora
  const uniqueStudentsFromRegistrations = useMemo(() => {
    return getUniqueStudentsWithTeam(
      registrations as unknown as Registration[]
    );
  }, [registrations]);

  // Aplica filtros de curso/unidade ao buscar registrations
  useEffect(() => {
    setRegistrationsFilters({
      courseId: filterCourseId,
      unitId: filterUnitId,
    });
  }, [filterCourseId, filterUnitId, setRegistrationsFilters]);

  // IDs dos estudantes já na aplicação, memoizada
  const alreadyInApplicationIds = useMemo(() => {
    return new Set(applicationStudents.map((as) => as.registration.studentId));
  }, [applicationStudents]);

  // Fecha o modal de duplicado automaticamente em 1s
  useEffect(() => {
    if (showDuplicateAlert) {
      const timer = setTimeout(() => {
        setShowDuplicateAlert(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showDuplicateAlert]);

  // Carregar turmas no AsyncSelect (com debounce), envolto em useCallback
  const loadTeams = useCallback(
    async (inputValue: string, callback: (options: Team[]) => void) => {
      setTeamsHookSearch(inputValue);
      callback(teams);
    },
    [teams, setTeamsHookSearch]
  );
  const debouncedLoadTeams = useMemo(
    () => debounce(loadTeams, 300),
    [loadTeams]
  );

  // Carregar alunos no AsyncSelect (com debounce), excluindo quem já está na aplicação
  const loadStudents = useCallback(
    async (
      inputValue: string,
      callback: (options: SelectedStudentData[]) => void
    ) => {
      const lower = inputValue.toLowerCase();

      const filtered = uniqueStudentsFromRegistrations.filter(
        (st) =>
          st.name.toLowerCase().includes(lower) ||
          st.email.toLowerCase().includes(lower)
      );

      const excluded = filtered.filter(
        (st) => !alreadyInApplicationIds.has(st.id)
      );

      callback(excluded);
    },
    [uniqueStudentsFromRegistrations, alreadyInApplicationIds]
  );
  const debouncedLoadStudents = useMemo(
    () => debounce(loadStudents, 300),
    [loadStudents]
  );

  // Lógica de remover alunos de turmas que deixaram de ser selecionadas
  const prevSelectedTeamsRef = useRef<Team[]>(selectedTeams);
  useEffect(() => {
    const prevSelectedTeams = prevSelectedTeamsRef.current;
    const removedTeams = prevSelectedTeams.filter(
      (prevTeam) => !selectedTeams.some((newTeam) => newTeam.id === prevTeam.id)
    );

    if (removedTeams.length > 0) {
      setSelectedStudents((prevStudents) =>
        prevStudents.filter((student) => {
          if (!student.team) return true;
          return !removedTeams.some(
            (t) => student.team && t.id === student.team.id
          );
        })
      );
    }
    prevSelectedTeamsRef.current = selectedTeams;
  }, [selectedTeams]);

  // Atualiza lista de turmas selecionadas
  const handleSelectTeamsChange = useCallback((newValue: MultiValue<Team>) => {
    setSelectedTeams(newValue as Team[]);
  }, []);

  // Remover turma individual
  const handleRemoveTeam = useCallback((teamId: string) => {
    setSelectedTeams((prev) => prev.filter((t) => t.id !== teamId));
  }, []);

  // Adicionar todos os alunos de uma turma
  const handleAddStudentsFromTeam = useCallback(
    async (teamId: string) => {
      try {
        const teamFull = await getTeamById(teamId);
        if (!teamFull?.students) return;

        const unifiedTeam = unifyTeamData(teamFull);
        const newStudents = teamFull.students.map<SelectedStudentData>(
          (st) => ({
            ...st,
            team: unifiedTeam,
          })
        );

        // Verifica duplicados
        const { uniqueNew, duplicates } = checkForDuplicatesAndFilter(
          newStudents,
          alreadyInApplicationIds,
          selectedStudents
        );

        if (duplicates.length > 0) {
          setShowDuplicateAlert(true);
        }

        // Adiciona somente os que não são duplicados
        setSelectedStudents((prev) => [...prev, ...uniqueNew]);
      } catch (e) {
        console.error("Erro ao adicionar alunos da turma:", e);
      }
    },
    [getTeamById, selectedStudents, alreadyInApplicationIds]
  );

  // Adicionar alunos individualmente via AsyncSelect
  const handleAddStudentIndividually = useCallback(
    (newStudents: SelectedStudentData[]) => {
      // Verifica duplicados
      const { uniqueNew, duplicates } = checkForDuplicatesAndFilter(
        newStudents,
        alreadyInApplicationIds,
        selectedStudents
      );

      if (duplicates.length > 0) {
        setShowDuplicateAlert(true);
      }

      setSelectedStudents((prev) => [...prev, ...uniqueNew]);
      setSelectedStudentOptions([]);
    },
    [selectedStudents, alreadyInApplicationIds]
  );

  // Remove aluno somente da lista local
  const handleRemoveStudentLocal = useCallback((studentId: string) => {
    setSelectedStudents((prev) => prev.filter((s) => s.id !== studentId));
  }, []);

  // Remove aluno que já está na aplicação (API)
  const handleRemoveStudentFromApplication = useCallback(
    async (applicationStudentId: string) => {
      try {
        await deleteApplicationStudent(applicationStudentId);
      } catch (err) {
        console.error("Erro ao remover aluno da aplicação:", err);
      }
    },
    [deleteApplicationStudent]
  );

  // Salvar todos os alunos da lista local
  const handleSave = useCallback(async () => {
    try {
      for (const student of selectedStudents) {
        // Note que student_id é o campo snake_case usado no banco
        await createApplicationStudent({ student_id: student.id });
      }
      navigate(`/tests/view/${examId}`);
    } catch (err) {
      console.error("Erro ao salvar alunos:", err);
    }
  }, [selectedStudents, createApplicationStudent, examId, navigate]);

  // Limpa referências do AsyncSelect quando um aluno é removido
  useEffect(() => {
    setSelectedStudentOptions((prev) =>
      prev.filter((sel) => selectedStudents.some((st) => st.id === sel.id))
    );
  }, [selectedStudents]);

  // Lista de alunos filtrada (busca + já na aplicação) - memoizada
  const filteredSelectedStudents = useMemo(() => {
    return selectedStudents.filter((st) => {
      // Filtro 1: remover quem já está na aplicação
      if (alreadyInApplicationIds.has(st.id)) return false;

      // Filtro 2: buscar por nome ou e-mail
      const lowerSearch = searchText.toLowerCase();
      return (
        st.name.toLowerCase().includes(lowerSearch) ||
        st.email.toLowerCase().includes(lowerSearch)
      );
    });
  }, [selectedStudents, searchText, alreadyInApplicationIds]);

  // Submit do form principal
  const onSubmitForm = (e: FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  return (
    <div className="row">
      {/* MODAL de Alerta de Duplicado (1s e fecha sozinho) */}
      <Modal
        show={showDuplicateAlert}
        onHide={() => setShowDuplicateAlert(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Atenção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Alguns alunos já estavam na lista ou na aplicação.
        </Modal.Body>
      </Modal>

      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              Gerenciar Alunos da Aplicação
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              Adicione ou remova alunos desta aplicação/prova
            </p>

            {/* Form principal */}
            <Form onSubmit={onSubmitForm}>
              <Row className="mb-4 align-items-end">
                {/* Filtro por Curso */}
                <Col md={3}>
                  <Form.Group controlId="selectCourse">
                    <Form.Label>Filtrar por Curso</Form.Label>
                    <Form.Select
                      value={filterCourseId || ""}
                      onChange={(e) =>
                        setFilterCourseId(e.target.value || undefined)
                      }
                    >
                      <option value="">Todos</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Filtro por Unidade */}
                <Col md={3}>
                  <Form.Group controlId="selectUnit">
                    <Form.Label>Filtrar por Unidade</Form.Label>
                    <Form.Select
                      value={filterUnitId || ""}
                      onChange={(e) =>
                        setFilterUnitId(e.target.value || undefined)
                      }
                    >
                      <option value="">Todas</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Adicionar aluno individualmente */}
                <Col md={6}>
                  <Form.Group controlId="addStudent">
                    <Form.Label>
                      Adicionar Aluno Individualmente <Attention />
                    </Form.Label>
                    <AsyncSelect<SelectedStudentData, true>
                      isMulti
                      cacheOptions
                      defaultOptions={uniqueStudentsFromRegistrations}
                      loadOptions={debouncedLoadStudents}
                      onChange={(value) => {
                        if (value) {
                          handleAddStudentIndividually(
                            value as SelectedStudentData[]
                          );
                        }
                      }}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.email})`
                      }
                      getOptionValue={(option) => option.id}
                      placeholder="Digite para buscar alunos..."
                      value={selectedStudentOptions}
                      styles={{
                        container: (base) => ({ ...base, width: "100%" }),
                        control: (base) => ({ ...base, minHeight: "38px" }),
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Caso não haja turmas */}
              {teams.length === 0 && (
                <div className="text-center my-3">
                  <p className="text-muted">
                    Nenhuma turma disponível. Adicione turmas primeiro ou
                    adicione alunos individualmente.
                  </p>
                </div>
              )}

              {/* Adicionar Turma */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="addTeam">
                    <Form.Label>
                      Adicionar Turma <Attention />
                    </Form.Label>
                    <AsyncSelect<Team, true>
                      isMulti
                      cacheOptions
                      defaultOptions={teams}
                      loadOptions={debouncedLoadTeams}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      placeholder="Digite para buscar turmas..."
                      onChange={(newValue) => handleSelectTeamsChange(newValue)}
                      value={selectedTeams}
                      isDisabled={teams.length === 0}
                      styles={{
                        container: (base) => ({ ...base, width: "100%" }),
                        control: (base) => ({ ...base, minHeight: "38px" }),
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Renderiza turmas selecionadas */}
              <TransitionGroup>
                {selectedTeams.length > 0 && (
                  <CSSTransition timeout={300} classNames="fade">
                    <div className="mb-4">
                      <h5>Turmas Selecionadas:</h5>
                      {selectedTeams.map((team) => (
                        <div
                          key={team.id}
                          className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <div>{team.name}</div>
                          <div className="d-flex">
                            <Button
                              variant="success"
                              size="sm"
                              className="me-2"
                              onClick={() =>
                                void handleAddStudentsFromTeam(team.id)
                              }
                            >
                              <FaUsers className="me-1" />
                              Adicionar Alunos
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveTeam(team.id)}
                            >
                              Remover Turma
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>

              {/* Lista de quem JÁ ESTÁ na aplicação */}
              <div className="mb-4">
                <h5>Alunos Já Cadastrados nesta Aplicação</h5>
                {applicationStudents.length === 0 && !loadingAppStudents ? (
                  <div className="text-center my-3">
                    <p className="text-muted">Nenhum aluno já cadastrado.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>E-mail</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applicationStudents.map((appStu) => {
                          const stu = appStu.registration.student;
                          return (
                            <tr key={appStu.id}>
                              <td>{stu.name}</td>
                              <td>{stu.email}</td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    void handleRemoveStudentFromApplication(
                                      appStu.id
                                    )
                                  }
                                >
                                  Remover da Aplicação
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Lista de Alunos que serão adicionados */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Alunos para Adicionar <Attention />
                </Form.Label>
                <Searchbar
                  search_input_value={searchText}
                  search_text="Filtrar por nome ou e-mail"
                  onChangeSearchInput={(value) => setSearchText(value)}
                  onSearchButtonClick={() => undefined}
                />
              </Form.Group>

              <TransitionGroup>
                {filteredSelectedStudents.length > 0 && (
                  <CSSTransition timeout={300} classNames="fade">
                    <div className="table-responsive mt-2">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Nome</th>
                            <th>E-mail</th>
                            <th>Unidade</th>
                            <th>Curso</th>
                            <th>Período</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSelectedStudents.map((student) => {
                            const team = student.team;
                            return (
                              <CSSTransition
                                key={student.id}
                                timeout={300}
                                classNames="fade"
                              >
                                <tr>
                                  <td>{student.name}</td>
                                  <td>{student.email}</td>
                                  <td>{team?.unit?.name || "-"}</td>
                                  <td>{team?.course?.name || "-"}</td>
                                  <td>
                                    {team
                                      ? team.is_first_semester
                                        ? "1"
                                        : "2"
                                      : "-"}
                                  </td>
                                  <td>
                                    <Actions
                                      actionItem={student}
                                      onDeleteClick={() =>
                                        handleRemoveStudentLocal(student.id)
                                      }
                                      hideManager
                                      hideEdit
                                    />
                                  </td>
                                </tr>
                              </CSSTransition>
                            );
                          })}
                          {filteredSelectedStudents.length === 0 && (
                            <tr>
                              <td
                                colSpan={6}
                                className="text-center text-muted"
                              >
                                Nenhum aluno selecionado
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>

              {/* Botões de Ação */}
              <div className="mt-4 d-flex justify-content-between">
                <DefaultButton
                  bg="secondary"
                  text="Voltar"
                  icon={<RiArrowGoBackLine />}
                  to={`/tests/view/${examId}`}
                  disabled={loadingAppStudents}
                />
                <DefaultButton
                  type="submit"
                  bg="confirm"
                  text="Salvar Alunos"
                  loadingtext="Salvando..."
                  loading={loadingAppStudents}
                  disabled={loadingAppStudents}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
